import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { SignInButton } from 'platform/user/authentication/components/DesignSystemSignInAndVerifyButtons';
import { AUTHN_SETTINGS } from 'platform/user/authentication/constants';
import {
  captureIntendedReturnUrl,
  getQueryParams,
} from 'platform/user/authentication/utilities';
import LoginButton, {
  loginHandler,
} from 'platform/user/authentication/components/LoginButton';
import { useFeatureToggle } from 'platform/utilities/feature-toggles';
import { signInAppCSS } from '../constants';
import '../sass/passkeys.scss';

export default function PasskeyInterim({ location, router }) {
  const { TOGGLE_NAMES, useToggleValue, useToggleLoadingValue } =
    useFeatureToggle();
  const clearSigninEnabled = useToggleValue(TOGGLE_NAMES.clearSigninEnabled);
  const designSystemSignInButtonsEnabled = useToggleValue(
    TOGGLE_NAMES.designSystemSignInButtonsEnabled,
  );
  const isLoading = useToggleLoadingValue();
  const LoginButtonType = designSystemSignInButtonsEnabled
    ? SignInButton
    : LoginButton;
  const csps = clearSigninEnabled
    ? ['clear', 'idme', 'logingov']
    : ['idme', 'logingov'];
  const providerList = clearSigninEnabled ? (
    <>
      <strong>
        <span aria-label="Clear">CLEAR</span>
      </strong>
      , <strong>ID.me</strong>, or <strong>Login.gov</strong>
    </>
  ) : (
    <>
      <strong>ID.me</strong> or <strong>Login.gov</strong>
    </>
  );

  const [priorReturnUrl] = useState(() => captureIntendedReturnUrl());

  const handleSignIn = (csp, useOAuth, queryParams) => {
    sessionStorage.setItem(
      AUTHN_SETTINGS.RETURN_URL,
      `${window.location.origin}/profile/account-security/passkeys`,
    );
    loginHandler(csp, useOAuth, queryParams);
  };

  const restoreReturnUrl = () => {
    sessionStorage.removeItem(AUTHN_SETTINGS.PASSKEY_PRIOR_RETURN_URL);
    sessionStorage.setItem(AUTHN_SETTINGS.PASSKEY_FOCUS_CLOSE, 'true');
  };

  useEffect(() => {
    if (location?.query?.oauth !== 'true') {
      router.replace({
        ...location,
        query: { ...location?.query, oauth: 'true' },
      });
    }
    document.title = 'Create a Passkey | Veterans Affairs';
  }, [location, router]);

  const {
    OAuth = true,
    clientId,
    codeChallenge,
    state,
    nonce,
    authorizeSsoId,
    prompt,
  } = getQueryParams();

  if (isLoading) {
    return (
      <section className="container row login vads-u-padding--3">
        <va-loading-indicator
          message="Loading your sign-in page..."
          set-focus
        />
      </section>
    );
  }

  return (
    <>
      <style>{signInAppCSS}</style>
      <section className="login login-page">
        <div className="container">
          <div className="row">
            <div className="usa-width-two-thirds medium-8 columns">
              <p>
                <va-link
                  back
                  href={priorReturnUrl}
                  text="Back to previous page"
                  onClick={restoreReturnUrl}
                />
              </p>
              <h1>Create a passkey</h1>
              <div className="passkey-interim-description va-introtext">
                <p>
                  To create a passkey, sign in with your {providerList} account
                  first. Then create a passkey in your{' '}
                  <strong>Account security</strong> settings. You can use your
                  passkey the next time you sign in to VA.gov.
                </p>
              </div>
              <div className="passkey-interim-buttons">
                {csps.map(csp => (
                  <LoginButtonType
                    key={csp}
                    csp={csp}
                    useOAuth={OAuth}
                    onClick={handleSignIn}
                    queryParams={{
                      clientId,
                      codeChallenge,
                      state,
                      nonce,
                      ...(authorizeSsoId && { authorizeSsoId }),
                      ...(prompt && { prompt }),
                    }}
                    actionLocation="passkey-interim"
                  />
                ))}
              </div>
              <h2>How passkeys work with your sign-in account</h2>
              <p>
                You can only create a passkey in your VA.gov profile if you have
                an identity-verified account with {providerList}. A passkey
                doesn’t replace your sign-in account, and you can continue to
                sign in with whatever option you prefer.
              </p>
              <p>
                <va-link
                  href="/resources/create-and-use-passkeys-on-vagov/"
                  text="Learn more about passkeys"
                  onClick={restoreReturnUrl}
                />
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

PasskeyInterim.propTypes = {
  location: PropTypes.object,
  router: PropTypes.object,
};
